import { useContext, useState } from "react"

import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { useI18next } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import Wave from "components/Banners/Wave"
import CTA from "components/CTA"
import InnerWrapper from "components/InnerWrapper"
import { ScreenContext } from "components/Providers"
import { ReactComponent as LI } from "images/global/LinkedIn.svg"
import { ReactComponent as Twit } from "images/global/Twitter.svg"
import { ReactComponent as YT } from "images/global/Youtube.svg"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { ColorType } from "types"
import { LocaleData } from "types/gatsby-alias"
import { fresponsive } from "utils/fullyResponsive"
import { isBrowser } from "utils/functions"
import useGetStaticTranslations from "utils/hooks/useGetStaticTranslations"
import links from "utils/links"
import UniversalLink from "utils/Loader/UniversalLink"
import routes, { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"

import LanguageButton from "./buttons/LanguageButton"
import HeroLogo from "./header/HeroLogo"
import { title } from "process"

const mainLinks = [
  {
    title: "Deposits",
    links: routes.deposits,
  },
  {
    title: "Engagement Channels",
    links: routes.channels,
  },
  {
    title: "Payments & Cards",
    links: routes.payments_and_cards,
  },
  {
    title: "Lending",
    links: routes.lending,
  },
  {
    title: "Risk",
    links: routes.risk,
  },
  {
    title: "Platform",
    links: routes.platforms,
  },
  {
    title: "Services",
    links: routes.services,
  },

  {
    title: "Company",
    links: routes.company,
  },
]

export default function Footer({
  waveBottomColor,
  pathname,
  languageOverride,
}: {
  waveBottomColor: ColorType
  pathname: string
  languageOverride?: string
}) {
  const images: Queries.FooterQuery = useStaticQuery(graphql`
    query Footer {
      footerBG: file(relativePath: { eq: "global/footerBG.webp" }) {
        ...LocalImageQuery
      }
      allLocale(filter: { ns: { eq: "menu" } }) {
        nodes {
          ns
          language
          data
        }
      }
    }
  `)
  const FooterBGPNG = images.footerBG?.childImageSharp?.gatsbyImageData
  const { language } = useI18next()
  const [showFollowDiv, setShowFollowDiv] = useState(true)

  const handleToggleFollowDiv = () => {
    setShowFollowDiv(!showFollowDiv)
  }

  const { mobile } = useContext(ScreenContext)
  const setWidth = useMedia("", "", "81.445vw", "")
  const [currentYear] = useState<number>(new Date().getFullYear())

  const localeDataJS: LocaleData = useGetStaticTranslations(
    "menu",
    languageOverride
  )

  const handleClick = () => {
    if (isBrowser()) {
      window.Osano.cm.showDrawer("osano-cm-dom-info-dialog-open")
    }
  }

  const allLinks = mainLinks.map(data => (
    <LinksDiv key={data.title}>
      <LinkHeader>{localeDataJS[data.title]}</LinkHeader>
      {data.links.map(link => {
        const localeLink = checkedLocaleLink(
          link.url,
          languageOverride ?? language
        )
        return (
          <LinkWrapper key={`link-${link.title}`}>
            <UniversalLink to={localeLink}>
              {localeDataJS[link.title]}
            </UniversalLink>
          </LinkWrapper>
        )
      })}
    </LinksDiv>
  ))

  const allLegalLinks = routes.legal.map((data, i) => {
    if (i === 0) {
      return (
        <LegalLink
          tabIndex={0}
          role="link"
          onKeyUp={handleClick}
          onClick={handleClick}
          key={`legal-${data.url}`}
        >
          {localeDataJS[data.title]}
        </LegalLink>
      )
    }
    const localeLink = checkedLocaleLink(data.url, languageOverride ?? language)
    return (
      <LegalLink key={`legal-${data.url}`}>
        <UniversalLink to={localeLink}>
          {localeDataJS[data.title]}
        </UniversalLink>
      </LegalLink>
    )
  })

  return (
    <FooterWrapper>
      <CTA translations={localeDataJS} languageOverride={languageOverride} />

      <InnerWrapper setWidth={setWidth}>
        <MainLinksWrapper>
          <CompanyInfo>
            <Frame>
              <HeroLogo languageOverride={languageOverride} />
              <CompanyAddress>
               © Galileo Financial Technologies, LLC {currentYear}
              </CompanyAddress>

              <CompanyIntro>
                {
                  localeDataJS[
                    "Galileo Financial Technologies, LLC is a technology company, not a bank. Galileo partners with many issuing banks to provide banking services in North and South America."
                  ]
                }
              </CompanyIntro>
            </Frame>
            <LanguageButton
              handleToggleFollowDiv={handleToggleFollowDiv}
              pathname={pathname}
              languageOverride={languageOverride}
            />
            {showFollowDiv && (
              <FollowDiv>
                <SocialHeader>{localeDataJS["Follow Galileo"]}</SocialHeader>
                <SocialDiv>
                  <SocialLink aria-label="to twitter" href={links.twitter}>
                    <Twitter />
                  </SocialLink>
                  <SocialLink aria-label="to youtube" href={links.youtube}>
                    <Youtube />
                  </SocialLink>
                  <SocialLink aria-label="to linkedin" href={links.linkedin}>
                    <LinkedIn />
                  </SocialLink>
                </SocialDiv>
              </FollowDiv>
            )}
          </CompanyInfo>
          <MainLinks>{allLinks}</MainLinks>
        </MainLinksWrapper>
        <LegalWrapper>{allLegalLinks}</LegalWrapper>
      </InnerWrapper>

      {FooterBGPNG && (
        <CardBG
          image={FooterBGPNG}
          alt="blurry cards illustration"
          loading="lazy"
        />
      )}

      <Wave bottomStopColors={[waveBottomColor]} />
    </FooterWrapper>
  )
}

const LinksDiv = styled.div`
  display: flex;
  flex-direction: column;

  ${media.tablet} {
    width: 15vw;
  }

  ${media.mobile} {
    width: 38.4vw;
  }
`
const LinkHeader = styled.p`
  ${text.buttonL}
  margin-bottom: 0.301vw;
  color: ${colors.white};

  ${media.fullWidth} {
    margin-bottom: 5px;
  }
  ${media.tablet} {
    margin-bottom: 0.45vw;
  }
  ${media.mobile} {
    ${text.bodyR};
  }
`
const LinkWrapper = styled.div`
  ${text.bodyS};

  ${media.fullWidth} {
    margin-top: 15px;
  }

  ${media.desktop} {
    margin-top: 0.904vw;
  }

  ${media.tablet} {
    margin-top: 1.465vw;
  }

  ${media.mobile} {
    ${text.bodyXS};
    margin-top: 2.667vw;
  }

  a {
    color: ${colors.blue50};
    :hover {
      transition-duration: 0.3s;
      color: ${colors.white};
    }
  }
`
const LegalLink = styled.div`
  ${text.bodyS};
  color: ${colors.blue200};
  a {
    ${text.bodyS};
    color: ${colors.blue200};
  }
  ${media.mobile} {
    ${text.bodyXXS};
    a {
      ${text.bodyXXS};
      color: ${colors.blue200};
    }
  }
  cursor: pointer;
`

const FooterWrapper = styled.div`
  position: relative;
  z-index: 4;
  display: flex;
  justify-content: center;
  width: 100%;
  height: 38.193vw;
  background-color: ${colors.blue800};

  ${media.fullWidth} {
    width: 100%;
    height: 639px;
  }

  ${media.tablet} {
    padding: 11.859vw 0 7.813vw 0;
    height: auto;
  }

  ${media.mobile} {
    flex-direction: column;
    padding: 5vw;
    height: auto;
  }
`

const MainLinksWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 86.747vw;
  position: relative;
  height: 16.867vw;
  margin-bottom: 11.205vw;
  margin-top: 4.217vw;

  ${media.fullWidth} {
    width: 1440px;
    height: 280px;
    margin-bottom: 186px;
    margin-top: 70px;
  }
  ${media.tablet} {
    height: auto;
    margin-bottom: 5.859vw;
    margin-top: 0;
  }

  ${media.mobile} {
    height: auto;
    flex-direction: column-reverse;
    align-items: space-between;
    gap: 60px;
    margin-top: 18vw;
    margin-bottom: 60px;
  }
`

const CompanyInfo = styled.div`
  display: inline-flex;
  height: fit-content;
  flex-direction: column;
  align-items: flex-start;
  width: 13.403vw;
  margin-right: 3vw;
  gap: 30px;
  ${media.fullWidth} {
    width: 193px;
    margin-right: 81px;
  }
  ${media.tablet} {
    width: 18.848vw;
    margin-right: 5.859vw;
  }

  ${media.mobile} {
    height: 335px;
    width: 89.333vw;
    margin-right: auto;
    display: flex;
    width: 375px;
    padding-left: 0px;
    flex-direction: column;
    align-items: flex-start;
    gap: 40px;
  }
`

const Frame = styled.div`
  ${fresponsive(css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  `)}
`
const CompanyAddress = styled.p`
  ${text.bodyS}

  color: ${colors.blue200};
  margin-top: 0.904vw;
  ${media.fullWidth} {
    margin-top: 15px;
  }
  ${media.tablet} {
    margin-top: 1.465vw;
  }

  ${media.mobile} {
    margin-top: 2.667vw;
  }
`

const CompanyIntro = styled.p`
  ${fresponsive(css`
    color: ${colors.blue200};
    ${text.bodyXXXS}
    width: 193px;
  `)}
`

const FollowDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.602vw;

  ${media.fullWidth} {
    gap: 10px;
  }
  ${media.tablet} {
    gap: 0.977vw;
  }
  ${media.mobile} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`

const SocialHeader = styled.p`
  ${text.buttonL}
  color: ${colors.white}
`

const SocialDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.602vw;

  ${media.fullWidth} {
    gap: 10px;
  }
  ${media.tablet} {
    gap: 0.977vw;
    margin-top: 0.977vw;
  }
  ${media.mobile} {
    gap: 2.667vw;
    margin-top: 2.667vw;
  }
`

const SocialLink = styled.a``

const Twitter = styled(Twit)`
  width: 1.506vw;
  height: 1.506vw;

  rect {
    fill: ${colors.white};
  }

  :hover {
    cursor: pointer;
    rect {
      fill: ${colors.blue400};
      transition-duration: 0.3s;
    }
    path {
      fill: ${colors.white};
      transition-duration: 0.3s;
    }
  }

  ${media.fullWidth} {
    width: 25px;
    height: 25px;
  }

  ${media.tablet} {
    width: 2.441vw;
    height: 2.441vw;
  }

  ${media.mobile} {
    width: 6.667vw;
    height: 6.667vw;
  }
`

const Youtube = styled(YT)`
  width: 1.506vw;
  height: 1.506vw;

  rect {
    fill: ${colors.white};
  }

  :hover {
    cursor: pointer;
    rect {
      fill: ${colors.blue400};
      transition-duration: 0.3s;
    }
    path {
      fill: ${colors.white};
      transition-duration: 0.3s;
    }
  }

  ${media.fullWidth} {
    width: 25px;
    height: 25px;
  }

  ${media.tablet} {
    width: 2.441vw;
    height: 2.441vw;
  }

  ${media.mobile} {
    width: 6.667vw;
    height: 6.667vw;
  }
`

const LinkedIn = styled(LI)`
  width: 1.506vw;
  height: 1.506vw;

  rect {
    fill: ${colors.white};
  }

  :hover {
    cursor: pointer;
    rect {
      fill: ${colors.blue400};
      transition-duration: 0.3s;
    }
    path {
      fill: ${colors.white};
      transition-duration: 0.3s;
    }
  }

  ${media.fullWidth} {
    width: 25px;
    height: 25px;
  }

  ${media.tablet} {
    width: 2.441vw;
    height: 2.441vw;
  }

  ${media.mobile} {
    width: 6.667vw;
    height: 6.667vw;
  }
`
const MainLinks = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.35vw;

  width: 70vw;
  height: 16.867vw;

  ${media.fullWidth} {
    gap: 22px;
    width: 1162px;
    height: 280px;
  }
  ${media.tablet} {
    flex-wrap: wrap;
    gap: 4.813vw;
    width: 54.883vw;
    height: auto;
  }

  ${media.mobile} {
    flex-wrap: wrap;
    width: 100%;
    height: auto;
    gap: 4.88vw 9vw;
  }
`

const LegalWrapper = styled.div`
  width: 100%;
  height: 1.265vw;
  display: flex;
  justify-content: center;
  color: ${colors.blue200};

  /* Default/Desktop styles */
  ${LegalLink} {
    /* Remove individual margins and use gap instead */
    margin-right: 0;
  }

  /* Use gap for consistent spacing */
  gap: 90px;

  ${media.fullWidth} {
    width: 100%;
    height: 21px;
    display: flex;
    justify-content: center;
    gap: 90px; /* Maintain consistent spacing */
  }

  ${media.tablet} {
    width: 100%;
    height: 2.051vw;
    margin: 0;
    gap: 8vw; /* Responsive gap for tablet */
  }

  ${media.mobile} {
    ${text.bodyXXS};
    width: 309px;
    height: fit-content;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 80px;
    margin: 0px 0px 20px 0px;

    ${LegalLink} {
      margin-right: 0; /* Remove any lingering margins */
    }
  }
`

const CardBG = styled(GatsbyImage)`
  position: absolute;
  width: 93.373vw;
  height: 51.205vw;
  right: 0;
  bottom: 1.145vw;
  z-index: 2;
  mix-blend-mode: overlay;

  ${media.fullWidth} {
    width: 1549.992px;
    height: 850.003px;
    right: 0px;
    bottom: 19px;
  }

  ${media.mobile} {
    display: none;
  }
`
